import _extends from '@babel/runtime/helpers/extends';
import PropTypes from 'prop-types';
import React from 'react';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { I as Illustration } from './Illustration-a7ce5d51.js';
import { p as props, d as defaults } from './componentEl-fcbc301e.js';
import { p as paddingPropTypes } from './spacing-0f39ba4a.js';
import { G as Group } from './Group-0add3181.js';
import { T as Typography } from './Typography-98688343.js';
import { W as Wrapper } from './Wrapper-2eba1576.js';
import { A as Avatar } from './Avatar-548d36a9.js';

/*
Component to display a single notification
*/

function NotificationsItem({
  avatar,
  timestamp,
  initials,
  children,
  nohover,
  highlight,
  source,
  ...otherProps
}) {
  const cssClasses = ['st_notification'];
  if (highlight) cssClasses.push('st_notification--highlight');
  if (nohover) cssClasses.push('st_notification--nohover');
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    gap: "sm",
    valign: "start",
    wrap: false,
    fullWidth: true
  }, (avatar || initials) && /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    initials: initials,
    margin: {
      top: 'sm'
    }
  }), /*#__PURE__*/React.createElement(Group.Item, {
    componentEl: Group,
    direction: "column",
    valign: "start",
    gap: "xs",
    grow: 1,
    shrink: 1
  }, /*#__PURE__*/React.createElement(Typography, {
    size: "md",
    color: "ink"
  }, children), timestamp && /*#__PURE__*/React.createElement(Typography, {
    variant: "note"
  }, timestamp)), source && /*#__PURE__*/React.createElement(Group.Item, {
    align: "flex-end",
    justify: "flex-end"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "note"
  }, source))));
}

// proptypes definitions
NotificationsItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** url to avatar, if available */
  avatar: PropTypes.string,
  /** timestamp for event */
  timestamp: PropTypes.string,
  /** whether component should look highlighted */
  highlight: PropTypes.bool,
  /** fallback initials for avatar */
  initials: PropTypes.string,
  /** whether to suppress hover for this item */
  nohover: PropTypes.bool,
  /** padding for item. Setting this overrides defaults and does not extend them */
  padding: paddingPropTypes,
  /** optional source for notification */
  source: PropTypes.string
};

// default props
NotificationsItem.defaultProps = {
  componentEl: defaults,
  avatar: null,
  highlight: false,
  timestamp: null,
  initials: null,
  nohover: false,
  padding: {
    vertical: 'md',
    horizontal: 'mlg'
  },
  source: null
};
NotificationsItem.displayName = 'Notifications.Item';

/*
Component to display list of notifications
and empty message if there are no notifications to display
*/

function Notifications({
  children,
  emptyTitle,
  emptyMessage,
  size,
  className,
  ...otherProps
}) {
  let cssClasses = [];
  if (React.Children.count(children) === 0) {
    cssClasses = ['st_notifications__empty', `st_notifications__empty--${size}`];
    cssClasses = addClassNames(className, cssClasses);
    return /*#__PURE__*/React.createElement("div", _extends({
      className: cssClasses.join(' ')
    }, otherProps), /*#__PURE__*/React.createElement(Illustration, {
      name: "no-notifications",
      className: "st_notifications__emptyimg"
    }), emptyTitle && /*#__PURE__*/React.createElement("h3", {
      className: "st_notifications__emptytitle"
    }, emptyTitle), emptyMessage && /*#__PURE__*/React.createElement("p", {
      className: "st_notifications__emptytext"
    }, emptyMessage));
  }
  cssClasses = addClassNames(className, ['st_notifications']);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

// proptypes definitions
Notifications.propTypes = {
  /** message to display if there are no notifications */
  emptyMessage: PropTypes.string,
  /** title to display if there are no notifications */
  emptyTitle: PropTypes.string,
  /** size of notifications */
  size: PropTypes.oneOf(['md', 'lg']),
  /** any extra CSS classes */
  className: classNamePropTypes
};

// default props
Notifications.defaultProps = {
  emptyMessage: null,
  emptyTitle: null,
  size: 'md',
  className: null
};
Notifications.Item = NotificationsItem;

export { Notifications as N };
